import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Smartphone, Monitor, Chrome, Share2 } from 'lucide-react';

interface InstallInstructionsProps {
  platform: 'ios' | 'android' | 'desktop' | 'unknown';
}

export function InstallInstructions({ platform }: InstallInstructionsProps) {
  if (platform === 'ios') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            Install on iPhone/iPad
          </CardTitle>
          <CardDescription>
            Follow these steps to add BuildCRM to your home screen
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted p-4 rounded-lg">
            <p className="text-sm text-muted-foreground mb-2">
              <strong>Note:</strong> Must use Safari browser
            </p>
          </div>
          <ol className="list-decimal list-inside space-y-3 text-sm">
            <li className="flex items-start gap-2">
              <span className="font-mono text-primary">1.</span>
              <span>Open BuildCRM in <strong>Safari</strong> browser</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-mono text-primary">2.</span>
              <span>
                Tap the <Share2 className="inline h-4 w-4 mx-1" /> <strong>Share</strong> button at the bottom of the screen
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-mono text-primary">3.</span>
              <span>Scroll down and tap <strong>"Add to Home Screen"</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-mono text-primary">4.</span>
              <span>Tap <strong>"Add"</strong> in the top right corner</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-mono text-primary">5.</span>
              <span>The BuildCRM icon will appear on your home screen</span>
            </li>
          </ol>
        </CardContent>
      </Card>
    );
  }

  if (platform === 'android') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            Install on Android
          </CardTitle>
          <CardDescription>
            Follow these steps to install BuildCRM as an app
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted p-4 rounded-lg">
            <p className="text-sm text-muted-foreground mb-2">
              <strong>Note:</strong> Works best in Chrome browser
            </p>
          </div>
          <ol className="list-decimal list-inside space-y-3 text-sm">
            <li className="flex items-start gap-2">
              <span className="font-mono text-primary">1.</span>
              <span>Open BuildCRM in <strong>Chrome</strong> browser</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-mono text-primary">2.</span>
              <span>
                Tap the <Chrome className="inline h-4 w-4 mx-1" /> <strong>menu</strong> (three dots) in the top right
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-mono text-primary">3.</span>
              <span>Select <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-mono text-primary">4.</span>
              <span>Confirm by tapping <strong>"Install"</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-mono text-primary">5.</span>
              <span>The BuildCRM app will appear in your app drawer</span>
            </li>
          </ol>
        </CardContent>
      </Card>
    );
  }

  if (platform === 'desktop') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Monitor className="h-5 w-5" />
            Install on Desktop
          </CardTitle>
          <CardDescription>
            Follow these steps to install BuildCRM on your computer
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ol className="list-decimal list-inside space-y-3 text-sm">
            <li className="flex items-start gap-2">
              <span className="font-mono text-primary">1.</span>
              <span>Look for the <strong>install icon</strong> in your browser's address bar</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-mono text-primary">2.</span>
              <span>Click the install icon or use the browser menu</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-mono text-primary">3.</span>
              <span>Confirm by clicking <strong>"Install"</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-mono text-primary">4.</span>
              <span>BuildCRM will open in its own window</span>
            </li>
          </ol>
        </CardContent>
      </Card>
    );
  }

  return null;
}
